import { NPCS } from '../../content/npcs';
import { ALL_QUESTS } from '../../content/quests';
import type { Game } from '../../game/Game';
import { registerQuests } from '../../game/quests';
import { registerPanel } from '../../ui/app/App';
import { bump, ui } from '../../ui/app/store';
import { setGame } from '../economy/ui';
import { autoQuestsReady, progressLine, settleQuests } from './logic';
import { MapPanel } from './MapPanel';
import { QuestsPanel } from './QuestsPanel';
import './quests.css';

export {
  ALL_QUESTS,
  MAIN_QUESTS,
  SIDE_QUESTS,
  WORLD_QUESTS,
  STORY_CHAPTERS,
} from '../../content/quests';
export { settleQuests, questStatus, journalGroups, DERIVED_FLAGS } from './logic';
export { zoneMapIcons, worldZone, questFocus } from './mapData';

let installedOn: Game | null = null;

/**
 * Quests & map: registers the main story, side and world quests, the quest
 * journal (J) and the map (M), and a small system that auto-accepts story
 * openers, settles objectives already satisfied by the world's state (opened
 * chests, attuned waystones, cleared dungeons, tutorial actions) and lets
 * NPCs comment on quests in progress.
 */
export function install(game: Game): void {
  if (installedOn === game) return;
  installedOn = game;
  setGame(game);
  registerQuests(ALL_QUESTS);
  registerPanel('quests', QuestsPanel);
  registerPanel('map', MapPanel);

  let t = 0;
  game.systems.push((g, dt) => {
    t += dt;
    if (t < 0.5) return;
    t = 0;
    const p = g.progression;
    if (!p || g.loading) return;
    let changed = false;
    for (const q of autoQuestsReady(p.char)) if (p.acceptQuest(q.id)) changed = true;
    if (settleQuests(p) > 0) changed = true;
    if (changed) {
      bump();
      g.store.saveSoon(g.account);
    }
  });

  // NPCs who only have their greeting to offer mention the quest you are on for them.
  let lastNpc: string | null = null;
  ui.dialogue.subscribe((d) => {
    if (!d) {
      lastNpc = null;
      return;
    }
    if (d.npcId === lastNpc) return;
    lastNpc = d.npcId;
    const p = game.progression;
    const greeting = NPCS[d.npcId]?.greeting;
    if (!p || d.index !== 0 || d.lines.length !== 1 || d.lines[0] !== greeting) return;
    const line = progressLine(p.char, d.npcId);
    if (!line) return;
    queueMicrotask(() => {
      if (ui.dialogue.value === d) ui.dialogue.value = { ...d, lines: [line] };
    });
  });
}
